export function toMinutes(timeStr) {
  const [h, m] = timeStr.split(':').map(Number)
  return h * 60 + m
}

export function getCurrentMinutes() {
  const now = new Date()
  return now.getHours() * 60 + now.getMinutes()
}

export const SHUTTLE_BOARDING_WINDOW_MINUTES = 5
export const SHUTTLE_DWELL_MINUTES = 5
export const DEFAULT_SHUTTLE_DURATION_MINUTES = 40

export function getShuttleRunState(item, now = getCurrentMinutes()) {
  const start = toMinutes(item.time ?? item.departureTime)
  const duration = Math.max(
    Math.round((item.durationSec ?? DEFAULT_SHUTTLE_DURATION_MINUTES * 60) / 60),
    SHUTTLE_DWELL_MINUTES + 1
  )
  const boardingStart = start - SHUTTLE_BOARDING_WINDOW_MINUTES
  const dwellEnd = start + SHUTTLE_DWELL_MINUTES
  const arrival = start + duration
  const arrivalDone = arrival + 1

  if (now < boardingStart) {
    const minutesLeft = start - now
    return {
      phase: 'pre_departure',
      tone: 'future',
      label: '출발 전',
      shortLabel: '출발 전',
      detail: `${minutesLeft}분 후 출발`,
      minutesLeft,
      progress: 0,
      isDone: false,
      isActive: false,
    }
  }

  if (now < start) {
    const minutesLeft = start - now
    return {
      phase: 'boarding',
      tone: 'next',
      label: '탑승 대기',
      shortLabel: '탑승 대기',
      detail: `${minutesLeft}분 후 출발`,
      minutesLeft,
      progress: 0,
      isDone: false,
      isActive: true,
    }
  }

  if (now < dwellEnd) {
    return {
      phase: 'dwell',
      tone: 'now',
      label: '정차 중',
      shortLabel: '정차 중',
      detail: `${dwellEnd - now}분 후 출발`,
      minutesLeft: 0,
      progress: 0,
      isDone: false,
      isActive: true,
    }
  }

  if (now < arrival) {
    const minutesToArrival = arrival - now
    return {
      phase: 'running',
      tone: 'running',
      label: '운행 중',
      shortLabel: '운행 중',
      detail: `도착까지 ${minutesToArrival}분`,
      minutesLeft: 0,
      progress: duration ? (now - start) / duration : 0.5,
      isDone: false,
      isActive: true,
    }
  }

  if (now < arrivalDone) {
    return {
      phase: 'arrived',
      tone: 'arrived',
      label: '도착',
      shortLabel: '도착',
      detail: '도착',
      minutesLeft: 0,
      progress: 1,
      isDone: false,
      isActive: true,
    }
  }

  return {
    phase: 'done',
    tone: 'past',
    label: '운행 완료',
    shortLabel: '완료',
    detail: '운행 완료',
    minutesLeft: 0,
    progress: 1,
    isDone: true,
    isActive: false,
  }
}

export const getShuttleStatus = getShuttleRunState

export function getNextShuttleTime(times = [], now = getCurrentMinutes()) {
  let best = null
  for (const time of times) {
    const depMin = toMinutes(time)
    if (!Number.isFinite(depMin) || depMin < now) continue
    if (!best || depMin < best.depMin) {
      best = { time, depMin, minutesLeft: depMin - now }
    }
  }
  return best
}

export function getShuttleTimeStatus(item, now = getCurrentMinutes(), nextTime = null) {
  const time = item?.time ?? item?.departureTime ?? item
  const depMin = toMinutes(time)

  if (!Number.isFinite(depMin)) {
    return {
      state: 'future',
      tone: 'future',
      label: '출발 전',
      shortLabel: '출발 전',
      detail: '출발 전',
      minutesLeft: null,
      isPast: false,
      isNext: false,
      isFuture: true,
      isDone: false,
      isActive: false,
    }
  }

  if (depMin < now) {
    return {
      state: 'past',
      tone: 'past',
      label: '출발 완료',
      shortLabel: '지난 셔틀',
      detail: '출발 완료',
      minutesLeft: 0,
      isPast: true,
      isNext: false,
      isFuture: false,
      isDone: true,
      isActive: false,
    }
  }

  const minutesLeft = depMin - now
  const isNext = nextTime === time
  return {
    state: isNext ? 'next' : 'future',
    tone: isNext ? 'next' : 'future',
    label: isNext ? '다음 셔틀' : '출발 전',
    shortLabel: isNext ? '다음' : '출발 전',
    detail: minutesLeft === 0 ? '지금 출발' : `${minutesLeft}분 후 출발`,
    minutesLeft,
    isPast: false,
    isNext,
    isFuture: !isNext,
    isDone: false,
    isActive: isNext,
  }
}

export function getNextShuttle(times) {
  if (!times || times.length === 0) return { next: null, minutesLeft: null, allPast: true }
  const next = getNextShuttleTime(times)
  if (!next) return { next: null, minutesLeft: null, allPast: true }
  return { next: next.time, minutesLeft: next.minutesLeft, allPast: false }
}

export function getRouteNextShuttle(route) {
  let best = null
  for (const stop of route.stops ?? []) {
    const { next, minutesLeft, allPast } = getNextShuttle(stop.times)
    if (!allPast && next !== null) {
      if (best === null || minutesLeft < best.minutesLeft) {
        best = { next, minutesLeft, stopName: stop.stopName }
      }
    }
  }
  return best ?? { next: null, minutesLeft: null, allPast: true, stopName: '' }
}
